/**
 * Bank color resolution.
 *
 * The chain (per VER-126):
 *   1. account.colorOverride                  — manual escape hatch (rare)
 *   2. account.item.connectorPrimaryColor     — Pluggy's official institution color
 *   3. hashToColor(account.item.connectorId)  — deterministic fallback palette
 *
 * No hardcoded `BANK_COLORS` map — that approach doesn't scale to Pluggy's
 * 200+ Brazilian institutions and goes stale on rebrands.
 */

import { contrastRatio, darken, hexWithAlpha } from './colors';
import { token } from './theme/tokens';
import { normalizeExternalColor, oklchToHex } from './theme/oklch';

export interface BankColorInput {
  connectorId?: number | null;
  connectorPrimaryColor?: string | null;
  colorOverride?: string | null;
}

// Same concept as category colors: a WIDE, maximally-distinct spread across the OKLCH
// wheel, varying hue AND lightness AND chroma so two banks-without-brand never look alike.
// Hue comes from a golden-angle walk of the connectorId (consecutive ids land far apart),
// remapped OUT of the reserved green/teal income band (hue ~113–228) to keep the app's
// color language consistent (green/teal = income); lightness/chroma rotate per id.
const BANK_L_C: ReadonlyArray<readonly [number, number]> = [
  [0.58, 0.17], [0.5, 0.19], [0.68, 0.14], [0.45, 0.16], [0.62, 0.18], [0.54, 0.19],
];
const GOLDEN_ANGLE = 137.50776405;
const INCOME_BAND_START = 113; // skip [113, 228] — reserved for income greens/teals
const INCOME_BAND_WIDTH = 115;

/**
 * Connector with no Pluggy `primaryColor` → a deterministic OKLCH color outside the income
 * band, so the fallback is themed, vivid, and distinct across institutions (rather than
 * cycling a short hand-picked list). The SAME connector always resolves to the SAME color.
 */
export function hashToColor(connectorId: number | null | undefined): string {
  if (connectorId == null || !Number.isFinite(connectorId)) return oklchToHex(0.45, 0.135, 25);
  const n = Math.abs(Math.trunc(connectorId));
  const t = ((n * GOLDEN_ANGLE) % 245); // golden-angle walk over the allowed (non-income) span
  const hue = t < INCOME_BAND_START ? t : t + INCOME_BAND_WIDTH; // skip the reserved arc
  const [L, C] = BANK_L_C[n % BANK_L_C.length] ?? BANK_L_C[0]!;
  return oklchToHex(L, C, hue);
}

/**
 * Pluggy returns `connectorPrimaryColor` as a bare 6-char hex (`fb6910`),
 * without the leading `#`. Normalise so downstream CSS / color helpers
 * receive a parseable value.
 */
function normaliseHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('#')) return trimmed;
  if (/^[0-9a-fA-F]{6}$/.test(trimmed) || /^[0-9a-fA-F]{3}$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  return trimmed; // already a valid CSS color (rgb()/hsl()/named) — pass through.
}

export function getBankColor(input: BankColorInput): string {
  // A manual override is a deliberate human choice — keep it verbatim. A real
  // Pluggy brand color is NORMALIZED through the engine (keep hue, render at the
  // system envelope — palette.md §07); an achromatic brand routes to neutral.
  // No brand at all → the categorical token hash.
  const override = normaliseHex(input.colorOverride);
  if (override) return override;

  const brand = normaliseHex(input.connectorPrimaryColor);
  if (brand) {
    if (/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(brand)) {
      const quartet = normalizeExternalColor(brand);
      return quartet ? quartet.accent : token('--neutral-500', '#909390');
    }
    return brand; // already a non-hex CSS color (rgb()/named) — pass through
  }

  return hashToColor(input.connectorId);
}

/**
 * Surface color suitable for a given mode.
 *
 * - `tinted`: 10%-alpha background for chip/tile surfaces. Always WCAG-safe
 *   because content over it is rendered in `color` (the original brand hex)
 *   on a near-white blend, not the source hex on white.
 * - `solid`: solid background intended for white text. Iteratively darkens
 *   the source until contrast vs `#FFFFFF` clears the WCAG AA threshold of
 *   4.5:1. Caps at 6 steps so we never end up at pure black.
 */
export function getBankSurfaceColor(hex: string, mode: 'tinted' | 'solid'): string {
  if (mode === 'tinted') return hexWithAlpha(hex, 0.10);
  let candidate = hex;
  // Cap at 10 iterations — covers extreme cases like BB (yellow #FFEF38)
  // which needs the most darkening to clear 4.5:1 vs white.
  for (let i = 0; i < 10 && contrastRatio(candidate, '#FFFFFF') < 4.5; i++) {
    candidate = darken(candidate, 0.10);
  }
  return candidate;
}
